import { ReactNode } from 'react'
import Link from 'next/link'
import { ExternalLink } from '@/atoms/ExternalLink'
import { usePifOverlay } from '../contexts/PifOverlayContext'

interface DavidButtonProps {
  children: ReactNode
  variant?: 'orange' | 'white' | 'dark' | 'cyan' | 'red' | 'beige'
  href?: string
  onClick?: () => void
  className?: string
  openPifOverlay?: boolean
}

export function DavidButton({
  children,
  variant = 'orange',
  href,
  onClick,
  className = '',
  openPifOverlay = false,
}: DavidButtonProps) {
  const { openOverlay } = usePifOverlay()
  const baseClasses =
    'box-border content-stretch flex gap-[6px] h-[48px] items-center justify-center min-w-[200px] px-[24px] py-[14px] relative rounded-[12px] shrink-0 cursor-pointer'

  const variantClasses = {
    orange: 'bg-[#ea6f13] hover:bg-[#d66412]',
    white: 'bg-white hover:bg-[#f5f5f5]',
    dark: 'bg-[#141447] hover:bg-[#1a1a5a]',
    cyan: 'bg-[#48c7d8] hover:bg-[#3ab5c7]',
    red: 'bg-[#c91800] hover:bg-[#b01600]',
    beige: 'bg-[#faf0e7] hover:bg-[#f0e6dd]',
  }

  const textColorClasses = {
    orange: 'text-white',
    white: 'text-[#141447]',
    dark: 'text-white',
    cyan: 'text-[#141447]',
    red: 'text-white',
    beige: 'text-[#141447]',
  }

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${className} transition-colors`

  const shouldOpenPifOverlay = openPifOverlay

  const handleClick = (e: React.MouseEvent) => {
    if (shouldOpenPifOverlay) {
      e.preventDefault()
      openOverlay()
    } else if (onClick) {
      onClick()
    }
  }

  const buttonContent = (
    <div
      className={`font-angel-royal relative flex min-h-px min-w-px shrink-0 grow basis-0 flex-col justify-center text-center text-[24px] lowercase not-italic leading-[0] tracking-[0.5px] ${textColorClasses[variant]}`}
    >
      <p className="leading-[24px] tracking-[1px]">{children}</p>
    </div>
  )

  if (href && !shouldOpenPifOverlay) {
    const isExternal = href.startsWith('http')
    const isInternalPath = href.startsWith('/')

    // External link
    if (isExternal) {
      return (
        <ExternalLink href={href} className={combinedClasses}>
          {buttonContent}
        </ExternalLink>
      )
    }

    // Internal link using Next.js Link
    if (isInternalPath) {
      return (
        <Link href={href} className={combinedClasses}>
          {buttonContent}
        </Link>
      )
    }

    // Fallback for anchor links or other
    return (
      <a href={href} className={combinedClasses}>
        {buttonContent}
      </a>
    )
  }

  return (
    <button onClick={handleClick} className={combinedClasses}>
      {buttonContent}
    </button>
  )
}
